import StateMachine from 'javascript-state-machine'
import { MediaType } from "../../common/enums"
import protocolSender from "../util/protocol-sender"
import { asyncWrapper, tracelog } from "../../common/common"
import MsgDispatcher from "../util/msg-dispatcher"
import logger from "../../common/logger"
import HstWebSocket from "../util/websocket"
import mediaManager from "../media/media-manager"
import videoManager from "../media/video-manager"
import shareManager from "../media/share-manager"
import audioManager from "../media/audio-manager"
import MediaStats from "./media-stats"

/**
 * 上行媒体通道（对应WebRTC中的一个PeerConnection）
 */
class UpPeerConnection extends MediaStats {
  constructor(bindId) {
    super()
    
    // 初始化状态机
    this._fsm()

    // WebRTC的PeerConnection
    this.pc = null

    this.mediaId = null

    this.mediaType = null

    // 由信令服务器分配，在信令上唯一标识一个PeerConnection
    this.bindId = bindId

    // 由媒体服务器分配，在媒体上唯一标识一个PeerConnection
    this.pcId = null

    // 媒体服务器地址，多路流复用同一个websocket连接
    this.streamServer = null

    // 防止谷歌 反复触发onnegotiationneeded 函数
    this.isNegotiating = false

    // PeerConnection中所包含的媒体信息, UpTrack:
    // {
    //   streamId: xxx,
    //   streamToken: xxx,
    //   mediaType: xxx,
    //   mediaId: xxx, 
    //   rtcSender: xxx, 
    //   mediaStream: xxx,
    //   track: xxx
    // }
    this.upTracks = new Array()

    // 本端同时广播了音视频，当第一个用户登录进来要接收流的时候，此时服务器会先后几乎同时
    // 通知本地发送音频流和视频流，两个通知的处理需要进行排队，否则可能会PC导致协商失败
    this.isCreatingPeerConnection = false
    this.sendNotifyParamCache = null

    // 对于屏幕共享来说，当前默认只会有屏幕共享流，后面如果屏幕共享中加入音频，则要重新设计
    this.isScreenShare = false

    // 与媒体服务器之间建立的websocket连接
    this.websocket = null
    
    // 用来保存异步处理的参数，存在以下三种情况：
    // 1. 如果是第一次发送媒体流，需要先创建websocket，websocket创建成功后发送登录消息
    // 2. 如果不是第一次发送媒体流，websocket已经存在，还是需要先发送登录发送通道的消息
    // 3. 服务器可能短时间内连续通知发送音频和视频，这两个消息需要串行处理，否则容易出错
    this.sendQueue = new Array()

    // 计费相关
    this.videoBillStarted = false

    // 协议处理
    this.msgDispatcher = new MsgDispatcher()
    this.msgDispatcher.addMsgHandler(19007, this._processLoginResp.bind(this))
    this.msgDispatcher.addMsgHandler(19009, this._processSdpNegotiation.bind(this))
    this.msgDispatcher.addMsgHandler(19011, this._processSetMagicRes.bind(this))
    this.msgDispatcher.addMsgHandler(19014, this._processStopSendStream.bind(this))
    this.msgDispatcher.addMsgHandler(19015, this._processResolutionNotify.bind(this))

    return this
  }

  /**
   * 获取视频统计数据
   */
  getVideoStats() {
    return this.pc ? this.stats.video : null
  }

  /**
   * 获取音频统计数据
   */
  getAudioStats() {
    return this.pc ? this.stats.audio : null
  }
  /**
   * 切换设备
   * @param {*} devId 
   */
  switchDevice(mediaType) {
    let foundTrack = null;
    this.upTracks.forEach(function(track, index, arr){
      if (track.mediaType == mediaType) {
        foundTrack = track;
        arr.splice(index, 1); // 删除指定Track
        return false; // 跳出forEach循环
      }
    });  

    if (!foundTrack){ // 找不到流 证明发送流失败  断开连接
      // console.warn("Cannot find video track while switch video device!");
      this.close()
      return;
    }

    // 先停止采集
    this._doRemoveTrack(foundTrack);

    // 需要异步处理，先将参数保存起来
    let params = {
      streamServer: null, 
      streamId: foundTrack.streamId,
      streamToken: foundTrack.streamToken,
      mediaType: mediaType,
      mediaId: foundTrack.mediaId
    }
    this.sendQueue.push(params);

    this._doStartSendStream(this.sendQueue[0]);
  }
  /**
   * 切换分辨率  说明：chrome 90以上浏览器 applyContraints 函数设置分辨率无效 需关闭硬件加速 （ps: 暂时无法解决，等待官方修复）
   */
  switchResolution (videoParam) {
    return new Promise((resolve, reject) => {
      let videoConstraints = {}
      if (videoParam) {
        videoConstraints.frameRate = { ideal: videoParam.frameRate || 15 };
        videoConstraints.width =  videoParam.width;
        videoConstraints.height =  videoParam.height;
        // videoConstraints.width = { min: 320, ideal: videoParam.width };
        // videoConstraints.height = { min: 180, ideal: videoParam.height };
      }
  
      this.upTracks.forEach((track) => {
        if (track.mediaType == MediaType.VIDEO) {
          // track.track.contentHint = 'detail'
          track.track.applyConstraints(videoConstraints).then(() => {
            logger.log('new resolution succeed')
            resolve()
          }).catch((reason) => {
            logger.wran(`new resolution ${reason} failed`)
            reject({
              code: -1,
              reason: reason
            })
          })
          return false; // 跳出forEach循环
        }
      });  
    })
  }

  /**
   * 开始发送媒体
   * @param {*} params 
   */
  @tracelog
  startSendStream(params) {
    if (!this._shouldSendMedia(params)) {
      return protocolSender.sendNotifySendStreamRes({
        streamId: params.streamId,
        result: 2, // FIXME: 错误码定义
        send: 1
      })
    }

    this.mediaId = params.mediaId // 保存媒体id
    this.mediaType = params.mediaType // 保存媒体类型

    // 保存网关地址
    this.streamServer = params.streamServer

    // 请求先缓存到队列
    this.sendQueue.push(params)
    logger.log("Add media to send queue: " + JSON.stringify(params))

    // 状态转移
    if (this.state == 'init') {
      this.doConnect()
    } else if (this.state == 'connected') {
      this.doLogin()
    } else {
      logger.warn("Do nothing!")
    }
  }

  /**
   * 停止发送媒体
   * @param {*} params 
   */
  @tracelog
  stopSendStream(params) {
    if (!this.pc) {
      logger.error("Invalid peer connection!")
      return
    }
    // fix bug 用于判断当前pc是不是无效的
    if (this.mediaType === params.mediaType) this.mediaType = null

    let foundTrack = this._removeUpTrack(params.mediaType, params.mediaId)
    if (!foundTrack){
      logger.warn("Try to stop send nonexsitent media. ", params)
      return
    }

    // 只可能有一路音频，停止发送后立即停止统计
    if (params.mediaType == MediaType.AUDIO) {
      this.stopAudioStats()
    }

    // 可能存在两路音频（变声），两路音频都停止发送后，才能移除PeerConnection中的track
    if (params.mediaType == MediaType.AUDIO) {
      if (!this._hasAudioTrack()) {
        this._doRemoveTrack(foundTrack)
      }
    } else {
      this._doRemoveTrack(foundTrack)
    } 
    
    // 由于服务器不会响应停止广播消息，取而代之的是，媒体网关会通过信令通道通知停止发送
    // 媒体流，因此对全局UpPeerConnection的资源管理只能放到UpPeerConnection内部
    if (this.upTracks.length == 0) {
      logger.log("No track left in up peer connection, then close it.")  
      this.doClose()
    }
  }

  /**
   * 是否已经没有要发送的媒体流
   */
  isEmptyPeerConnection(){
    return (this.upTracks.length == 0)
  }

  /**
   * 收到服务器发送过来的Offer
   * @param {*} sdp 
   */
  _onRecvSdpAnswer(sdp){
    let bitRate = 0
    if (this.isScreenShare) {
      bitRate = shareManager.getScreenShareBitRate()
    } else {
      for (const track of this.upTracks) {
        if (track.mediaType == MediaType.VIDEO) {
          bitRate = videoManager.getVideoBitRate(track.mediaId)
        }
      }
    }

    if (bitRate) {
      sdp = this._setBitRateConstraint(sdp, bitRate)
      logger.log("Set max bitrate to " + bitRate + "kbps.")
    }

    this.pc.setRemoteDescription(
      new RTCSessionDescription({ type: "answer", sdp: sdp })).then(()=>{
      logger.log("Set remote desc success!")
    }).catch((error)=>{
      logger.error("Set remote desc failed!" + error)
    })
  }

  /**
   * 收到服务器发送的candidate
   * @param {*} data 
   */
  _onRecvCandidate(data){
    var iceCandidate = new RTCIceCandidate({
      sdpMLineIndex: data.sdpMLineIndex,
      candidate: data.candidate
    })

    this.pc.addIceCandidate(iceCandidate).then(()=>{
      logger.log("Add candidate success!")
    }).catch((error)=>{
      logger.error("Add candidate failed!" + error)
    })
  }

  /**
   * 关闭连接，销毁资源
   */
  close() {
    logger.log("Close up peer connection!")

    // 停止统计
    this.stopMediaStats()

    // 移除PeerConnection
    mediaManager.removeUpPc(this.bindId)

    // 关闭Track以停止对摄像头的占用
    for (const upTrack of this.upTracks) {
      if (upTrack.mediaType != 0) { // 屏幕共享流不能停止
        this._destroyStream(upTrack.mediaStream)
      }
    }

    if (this.pc) {
      this._stopSendAllStreams()
      this.pc.close()
      this.pc = null
    }

    if (this.websocket) {
      this.websocket.close()
      this.websocket = null
    }

    this.sendQueue = null
  }

  /**
   * 关闭Stream上track
   * @param {*} stream 
   */
  _destroyStream(stream) {
      for (const streamTrack of stream.getTracks()) {
        streamTrack.stop()
      }
  }
  
  /**
   * 移除PeerConnection上的所有track
   */
  _stopSendAllStreams() {
    for (const track of this.upTracks) {
      if (track.rtcSender) {
        this.pc.removeTrack(track.rtcSender)
      }
    }
    this.upTracks = []
  }

  /**
   * 媒体是否已经存在
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  _isMediaExist(mediaType, mediaId){
    for (const track in this.upTracks){
      if (track.mediaType == mediaType && track.mediaId == mediaId){
        return true
      }
    }
    return false
  }
  
  /**
   * 添加UpTrack
   * @param {*} stream 
   * @param {*} params 
   */
  _addTracks(stream, params) {
    for (const track of stream.getTracks()){
      // 解决谷歌bug 硬件加速情况下 分辨率上不去的问题
      if (track.kind === 'video') {
        track.contentHint = 'detail'
        // track.contentHint = 'motion'
      }
      let sender = this.pc.addTrack(track, stream)
      this.upTracks.push({
        mediaType: params.mediaType,
        mediaId: params.mediaId,
        rtcSender: sender,
        mediaStream: stream,
        track: track,
        streamId: params.streamId,
        streamToken: params.streamToken 
      })
      if (params.mediaType == MediaType.AUDIO 
        && params.mediaId.indexOf("_magic") != -1 && audioManager.audioMagicValue !== 0) {
        protocolSender.SendSetAudioMagicReq(
          this.websocket,
          params.streamId,
          audioManager.audioMagicValue)
      }
      // 视频计费开始通过服务器通知分辨率变化来触发
      if (params.mediaType == MediaType.AUDIO) {
        this._reportAudioBillStart(params.streamId)
      }

      logger.log("Add track to up pc, mediaType = " + 
        params.mediaType + ", mediaId = " + params.mediaId)
    }
  }

  /**
   * 创建PeerConnection
   */
  _createPeerConnection() {
    try {
      //this.pc = new RTCPeerConnection({sdpSemantics: "plan-b"})
      this.pc = new RTCPeerConnection({sdpSemantics: "unified-plan"})
      this.pc.onicecandidate = this._onIceCandidate.bind(this)
      this.pc.onnegotiationneeded = this._onNegotiationneeded.bind(this)
      this.pc.onsignalingstatechange = this._onSignalingstatechange.bind(this)
      this.pc.onconnectionstatechange = this._onConnectionStateChange.bind(this)

      this.startPcStats(this.pc, false)
    } catch (e) {
      logger.error("Failed to create peer connection! " + e.message)
    }
  }

  /**
   * PeerConnection连接状态变化通知
   * @param {*} event 
   */
  _onConnectionStateChange(event) {
    logger.log("Current peer connection state: " + this.pc.connectionState)

    if (this.state == 'negotiating') {
      if (this.pc.connectionState == 'connected' 
        || this.pc.connectionState == 'disconnected'
        || this.pc.connectionState == 'closed') {
        this.doneNegotiate()
        logger.log("Current send queue size: " + this.sendQueue.length)
      }
    } else {
      if (this.pc.connectionState == 'disconnected'
        || this.pc.connectionState == 'failed'
        || this.pc.connectionState == 'closed') {
        logger.warn("Unexpected state, then close up peer connection!")
        this.close()
      }
    }    
  }

  /**
   * PeerConnection协议状态变化通知
   */
  _onSignalingstatechange() {
    if (this.pc) {
      logger.log("Current signaling state: " + this.pc.signalingState)
      this.isNegotiating = (this.pc.signalingState != "stable")
      if (this.pc.signalingState == "stable") {
        this.sendQueue.shift() // 协商完成，正常情况协议会比媒体先协商完成
      }
    }
  }

  /**
   * WebRTC触发开始进行协商
   */
  @tracelog
  _onNegotiationneeded() {
    this.pc.createOffer().then(description => {
      if (description.sdp.indexOf('m=video') != -1 ||  description.sdp.indexOf('m=audio') != -1) {
        this.pc.setLocalDescription(description)
        protocolSender.sendSdpInfo(this.websocket, description, this.pcId)
      } else {
        logger.warn("Invalid sdp: " + description.sdp)
      }
    }).catch(error => {
      logger.error(error)
    })
  }

  /**
   * WebRTC产生的本地Candidate，要发送给服务器
   * @param {*} event 
   */
  _onIceCandidate(event) {
    if (event.candidate) {
      protocolSender.sendCandidate(this.websocket, event, this.pcId)
    } else {
      logger.log(JSON.stringify(event))
    }
  }

  /**
   * 在已有的PeerConnection上发送媒体
   * @param {*} params 
   */
  @tracelog
  _startSendAvStreamWithExistPc(params) {
    try {
      let constraints = mediaManager.constructConstraints(params)
      navigator.mediaDevices.getUserMedia(constraints).then(stream => {
        this._addTracks(stream, params)
        if (params.mediaType == MediaType.AUDIO) {
          this.startAudioStats(stream)
        }
      }).catch(err => {
        logger.error("getUserMedia failed!" + err)
      })
    } catch (err) { // 
      this.doError()
    }  
  }

  /**
   * 在新建的PeerConnection上发送媒体
   * @param {*} params 
   */
  @tracelog
  _startSendAvStreamWithNewPc(params) {
    let constraints = mediaManager.constructConstraints(params)
    if (!constraints) {
      logger.error("Inavlid constraints, params: " + JSON.stringify(params))
      return
    }
    if (!this.pc){
      this._createPeerConnection()
    }
    // 打开摄像头
    navigator.mediaDevices.getUserMedia(constraints).then(stream => {
      logger.log("Get user media success, params: " + JSON.stringify(params))
      window.localStream = stream
      // FIXME: 主动发送Offer（addTrack触发）
      this._onNegotiationneeded(params)
      this._addTracks(stream, params)
      if (params.mediaType == MediaType.AUDIO) {
        this.startAudioStats(stream)
      }
    }).catch(err => {
      logger.error("getUserMedia failed, err: " + JSON.stringify(err))
    })
  }
  /**
   * 开始发送音视频媒体流
   * @param {*} params 
   */
  @tracelog
  _startSendAvStream(params) {
    if (this.pc) {
      this._startSendAvStreamWithExistPc(params)
    } else {
      this._startSendAvStreamWithNewPc(params)
    }
  }

  /**
   * 开始发送屏幕共享媒体流
   * @param {*} params 
   */
  @tracelog
  async _startSendScreenShareStream(params) {
    if (!this.pc){
      this._createPeerConnection()
    }

    try {
      let stream = await shareManager.getScreenShareStream()
      if (stream) {
        // FIXME: 主动发送Offer（addTrack触发）
        this._onNegotiationneeded()
        this._addTracks(stream, params)
        this.isScreenShare = true
      } else {
        logger.log("Invalid stream!")
      } 
    } catch(err) {
      logger.error("Get screen share stream failed!")
    }
  }

  /**
   * 设置码率
   * @param {*} sdp 
   * @param {*} bandwidth 
   */
  _setBitRateConstraint(sdp, bandwidth) {

    let modifier = 'AS'

    // FIXME: 有些场景下识别不了adapter，还没搞清楚是什么原因，这里只能先注释掉
    // if (adapter.browserDetails.browser === 'firefox') {
    if (navigator && navigator.userAgent.indexOf("Firefox") > 0) {
      bandwidth = (bandwidth >>> 0) * 1000
      modifier = 'TIAS'
    }
  
    // TODO: 限速行应该放在哪里，是否有兼容性问题？
    if (sdp.indexOf('b=' + modifier + ':') === -1) {
      // insert b= after c= line.
      sdp = sdp.replace(/c=IN (.*)\r\n/g, 'c=IN $1\r\nb=' + modifier + ':' + bandwidth + '\r\n')
      //sdp = sdp.concat('b=' + modifier + ':' + bandwidth + '\r\n')
    } else {
      sdp = sdp.replace(new RegExp('b=' + modifier + ':.*\r\n'), 'b=' + modifier + ':' + bandwidth + '\r\n')
    }

    return sdp
  }

  /**
   * WebSocket连接成功处理
   * @param {*} event 
   */
  _onWebsocketOpen(event) {
    logger.log("Up peer connetion websocket created.")
    this.connectSuccess()
  }

  /**
   * WebSocket连接错误处理
   * @param {*} event 
   */
  _onWebsocketError(event) {
    logger.error("Up peer connection websocket error!")
    this.connectFailed()
  }

  /**
   * WebSocket连接关闭处理
   * @param {*} event 
   */
  _onWebsocketClosed(event) {
    logger.log("Up peer connection websocket closed!")
    if (this.state == 'connected') {
      this.doClose()
    } else {
      if (this.state != 'closed') {
        logger.warn("Up peer connection websocket closed exceptionally!")
      } 
    }
  }

  /**
   * 是否已经存在音频Track
   * @param {*} params 
   */
  _hasAudioTrack(params) {
    for (const track of this.upTracks) {
      if (track.mediaType == MediaType.AUDIO) {
        return true
      }
    }
    return false
  }

  /**
   * 登录发送通道响应
   * @param {*} msg 
   */
  _processLoginResp(msg) {
    if (msg.result == 0) {
      this.loginSuccess(msg)
    } else {
      this.sendQueue.shift()
      logger.warn("Login send channel failed, send queue size: " + this.sendQueue.length)
      this.loginFailed()
    }
  }

  /**
   * 收到sdp协商信令处理
   * @param {*} msg 
   */
  _processSdpNegotiation(msg) {
    if (msg.type) { // 存在type字段，说明是SDP协商消息
      if (msg.type === "answer") {
        this._onRecvSdpAnswer(msg.sdp)
      } else {
        logger.warn("Unknown msg type: " + msg.type)
      }
    } else { // 没有type字段，是Candidate消息
      if (msg.candidate) {
        this._onRecvCandidate(msg)
      } else {
        logger.log("Unexpected msg without candidate!")
      }
    }
  }

  /**
   * 媒体网关通知停止发送媒体处理
   * @param {*} msg 
   */
  _processStopSendStream(msg) {
    for (const track of this.upTracks) {
      if (track.streamId == msg.stream_id) {
        this.stopSendStream({
          mediaType: track.mediaType,
          mediaId: track.mediaId
        })
        break
      }
    }
  }

  /**
   * 设置变声响应
   * @param {*} msg 
   */
  _processSetMagicRes(msg) {
    if (msg.result != 0) {
      logger.error("Set magic audio failed!")
    }
  }

  /**
   * 媒体网关通知视频分辨率变化，需要从信令网关上报上去
   * @param {*} msg 
   */
  _processResolutionNotify(msg) {
    let params = {
      streamId: msg.stream_id,
      width: msg.width,
      height: msg.height,
      channelType: 0, // send
      reportType: this.videoBillStarted ? 1/* change */ : 0/* start */
    }

    if (!this.videoBillStarted) {
      this.videoBillStarted = true
    }

    // 对于视频来说，说明流已经通了，收到消息直接上报
    protocolSender.sendResolutionReport(this.websocket, params)
  }

  /**
   * 
   * @param {*} streamId 
   */
  _reportAudioBillStart(streamId) {
    let params = {
      streamId: streamId,
      width: 0,
      height: 0,
      channelType: 0, // send
      reportType: 1 /* start */
    }
    protocolSender.sendResolutionReport(this.websocket, params)
  }

  /**
   * 通知计费停止
   * @param {*} streamId 
   */
  _reportBillStop(streamId) {
    let params = {
      streamId: streamId,
      width: 0,
      height: 0,
      channelType: 0, // send
      reportType: 2 /* stop */
    }
    protocolSender.sendResolutionReport(this.websocket, params)
  }

  /**
   * 
   * @param {*} params 
   */
  _doStartSendStream(params) {
    if (params.mediaType === MediaType.SCREEN_SHARE){
      this._startSendScreenShareStream(params)
    } else if (params.mediaType === MediaType.AUDIO || params.mediaType === MediaType.VIDEO) {
      this._startSendAvStream(params)
    } else {
      logger.error("Invalid media type: " + params.mediaType)
    }
  }

  /**
   * 
   * @param {*} track 
   */
  _doRemoveTrack(track) {
    if (track.rtcSender){
      this.pc.removeTrack(track.rtcSender)
      this._reportBillStop(track.streamId) // 停止计费
      logger.log("Remove track, mediaType: " + track.mediaType + ", mediaId: " + track.mediaId)
    } 
  
    // ScreenShare的MediaStream不能销毁，由Store全局管理，否则会出现远端刷新退出
    // 分组本地屏幕共享广播画面黑屏，用户再进入分组，无法接收到屏幕共享的问题
    if (track.mediaStream) {
      if (track.mediaType !== MediaType.SCREEN_SHARE) {
        this._destroyStream(track.mediaStream)
      } else {
        shareManager.releaseScreenShareStream()
      }
    }
  }

  /**
   * 移除UpTrack
   * @param {*} mediaType 
   * @param {*} mediaId 
   */
  _removeUpTrack(mediaType, mediaId) {
    let foundTrack = null
    this.upTracks.forEach(function(track, index, arr) {
      if (track.mediaType == mediaType && track.mediaId == mediaId) {
        foundTrack = track
        arr.splice(index, 1) // 删除指定Track
        return false // 跳出forEach循环
      }
    })
    return foundTrack
  }

  /**
   * 是否存在Audio Track
   */
  _hasAudioTrack() {
    let hasAudioTrack = false
    for (const track of this.upTracks) {
      if (track.mediaType == MediaType.AUDIO) {
        hasAudioTrack = true
        break
      }
    }
    return hasAudioTrack
  }

  /**
   * 通知开始发送媒体的参数检查
   * @param {*} params 
   */
  _shouldSendMedia(params) {
    // 检查是否重复通知
    if (this._isMediaExist(params.mediaType, params.mediaId)){
      logger.warn("The media already exists. " + params)
      return false
    }

    // 通知发送变声，本地未打开变声，直接响应错误  fixed bug 默认发送变声_magic
    // if (params.mediaType == MediaType.AUDIO 
    //   && params.mediaId.indexOf("_magic") != -1
    //   && audioManager.audioMagicValue == 0) {
    //   logger.warn("Notify to send magic audio, but not allowed!")
    //   return false
    // }

    // 同一个PeerConnection通知发送的网关服务器不一样
    if (this.streamServer && this.streamServer !== params.streamServer) {
      logger.error("Unexpected stream servers: " + params.streamServer)
      return false
    }

    return true
  }
}

//------------------------------------------------------------------------------
// 状态机
//------------------------------------------------------------------------------
StateMachine.factory(UpPeerConnection, {
  init: 'init',
  transitions: [
    {name: 'doConnect',      from: 'init',        to: 'connecting'},  // 连接信令网关
    {name: 'connectSuccess', from: 'connecting',  to: 'connected'},   // 连接信令网关成功
    {name: 'connectFailed',  from: 'connecting',  to: 'init'},        // 连接信令网关失败
    {name: 'doLogin',        from: 'connected',   to: 'logining'},    // 登录媒体网关
    {name: 'loginSuccess',   from: 'logining',    to: 'logined'},     // 登录媒体网关成功
    {name: 'loginFailed',    from: 'logining',    to: 'connected'},   // 登录媒体网关失败
    {name: 'doSend',         from: 'logined',     to: 'negotiating'}, // 开始发送流
    {name: 'doneLogin',      from: 'logined',     to: 'connected'},   // 无需发送媒体（第二路音频流）
    {name: 'doneNegotiate',  from: 'negotiating', to: 'connected'},   // 完成媒体协商
    {name: 'doClose',        from: 'connected',   to: 'closed'},      // 关闭
    {name: 'doError',        from: 'negotiating', to: 'errored'}      // 获取媒体流失败
  ],
  methods: {
    /**
     * 打印状态转移信息
     * @param {*} lifecycle 
     */
    onTransition(lifecycle) {
      logger.state("transition: " + lifecycle.transition + ", from " + lifecycle.from + " to " + lifecycle.to)
    },

    /**
     * connecting状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onConnecting(lifecycle, params) {
      this.websocket = new HstWebSocket({
        addrUrl: [this.streamServer],
        handler: this.msgDispatcher,
        opened: this._onWebsocketOpen.bind(this),
        closed: this._onWebsocketClosed.bind(this),
        error: this._onWebsocketError.bind(this)
      })
    },

    /**
     * connected状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onConnected(lifecycle, params) {
      if (this.sendQueue.length > 0) {
        asyncWrapper(()=>{this.doLogin()})
      } else {
        if (lifecycle.from != 'connecting') {
          if (this.isEmptyPeerConnection()) {
            this.close()
          }
        } else {
          logger.error("Empty send queue!")
        }
      }
    },

    /**
     * logining状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogining(lifecycle, params) {
      console.assert(this.sendQueue.length > 0)
      protocolSender.sendLoginSendChannelReq(
        this.websocket, 
        this.sendQueue[0], 
        this.bindId)
    },

    /**
     * logined状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onLogined(lifecycle, params) {
      console.assert(this.sendQueue.length > 0)
  
      // 响应通知发送流
      protocolSender.sendNotifySendStreamRes({
        streamId: this.sendQueue[0].streamId,
        result: 0,
        send: 1
      })
  
      let sendItem = this.sendQueue[0]

      // 存在变音和原音两路音频流，对于Web来说，变声是在网关处理的，本地永远只需要发送
      // 一路原声即可，但是逻辑上可能存在两路音频流，第二路音频流不需要进行发送处理
      if (sendItem.mediaType == MediaType.AUDIO && this._hasAudioTrack(sendItem)) {
        let audioTrack = null
        for (const track of this.upTracks) {
          if (track.mediaType == MediaType.AUDIO) {
            audioTrack = track
            break
          }
        }
  
        console.assert(audioTrack != null)
        logger.log("The second audio stream: " + JSON.stringify(sendItem))

        this.upTracks.push({
          mediaType: sendItem.mediaType,
          mediaId: sendItem.mediaId,
          rtcSender: audioTrack.rtcSender,
          mediaStream: audioTrack.mediaStream,
          track: audioTrack.track,
          streamId: sendItem.streamId,
          streamToken: sendItem.streamToken 
        })
        this.sendQueue.shift()
  
        // 如果此路音频是变声的话，则设置下变声
        if (sendItem.mediaId.indexOf("_magic") != -1 && audioManager.audioMagicValue !== 0) {
          protocolSender.sendSetAudioMagic(
            this.websocket, 
            sendItem.streamId, 
            audioManager.audioMagicValue)
        }

        // 不需要协商，无条件转移到connected状态
        asyncWrapper(()=>{this.doneLogin()})
      } else {
        this.pcId = params.pc_id
        asyncWrapper(()=>{this.doSend()})
      }
    },

    /**
     * negotiating状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onNegotiating(lifecycle, params) {
      console.assert(this.sendQueue.length > 0)
      this._doStartSendStream(this.sendQueue[0])
    },

    /**
     * closed状态处理
     * @param {*} lifecycle 
     * @param {*} params 
     */
    onClosed(lifecycle, params) {
      this.close()
    }
  }
})

export default UpPeerConnection
